import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { CalendarDays, Eye, Loader2 } from "lucide-react";
import { listPosts, tickAutoBlog } from "@/lib/blog.functions";
import { SiteFooter, SiteHeader } from "@/components/site-chrome";
import { Reveal, ScrollProgress, WordsReveal } from "@/components/motion-kit";
import { Badge } from "@/components/ui/badge";
import { AdSlot } from "@/components/ad-slot";

export const Route = createFileRoute("/blog/")({
  head: () => ({
    meta: [
      { title: "Blog jasasebar — Strategi Promosi Jasa & Digital Marketing" },
      {
        name: "description",
        content:
          "Panduan praktis promosi jasa di Indonesia: SEO lokal, iklan berbayar, copywriting, dan cara memaksimalkan papan peringkat jasasebar.",
      },
      { property: "og:title", content: "Blog jasasebar — Strategi Promosi Jasa" },
      {
        property: "og:description",
        content: "Artikel dan panduan promosi jasa online yang bisa langsung dipraktikkan.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [
      { rel: "canonical", href: "https://www.jasasebar.com/blog" },
      { rel: "alternate", type: "application/rss+xml", title: "RSS Blog jasasebar", href: "/rss.xml" },
      { rel: "alternate", type: "application/atom+xml", title: "Atom Blog jasasebar", href: "/feed.xml" },
    ],
  }),
  component: BlogIndex,
});

function BlogIndex() {
  const fn = useServerFn(listPosts);
  const tick = useServerFn(tickAutoBlog);
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["blog"], queryFn: () => fn() });

  // Auto blog harian: pemicu ringan saat halaman blog dibuka (dikunci 1x/24 jam di server).
  useEffect(() => {
    let alive = true;
    tick()
      .then((r) => {
        if (alive && r.ran) queryClient.invalidateQueries({ queryKey: ["blog"] });
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [tick, queryClient]);
  const posts = data ?? [];

  return (
    <div className="min-h-screen">
      <ScrollProgress />
      <SiteHeader />

      <section className="hero-canvas border-b border-border">
        <div className="mx-auto max-w-5xl px-4 py-12">
          <h1 className="font-display text-3xl font-semibold sm:text-4xl">
            <WordsReveal text="Blog jasasebar" />
          </h1>
          <p className="mt-3 max-w-2xl text-sm text-muted-foreground">
            Taktik promosi jasa, studi kasus peringkat, dan panduan pembayaran digital — ditulis untuk pemilik usaha
            Indonesia.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-4 py-10">
        {isLoading && (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" /> Memuat artikel…
          </p>
        )}
        {!isLoading && posts.length === 0 && (
          <p className="surface-card p-6 text-sm text-muted-foreground">
            Belum ada artikel terbit. Tim redaksi sedang menyiapkan panduan pertama.
          </p>
        )}
        <AdSlot className="mb-4" label="Iklan" />
        <div className="grid gap-4 md:grid-cols-2">
          {posts.map((p) => (
            <Reveal key={p.id} className="surface-card overflow-hidden">
              <Link to="/blog/$slug" params={{ slug: p.slug }} className="block">
                {p.cover_url && (
                  <img
                    src={p.cover_url}
                    alt={p.title}
                    loading="lazy"
                    className="h-40 w-full object-cover"
                  />
                )}
                <div className="p-5">
                  <Badge variant="secondary" className="text-[11px]">
                    {p.category}
                  </Badge>
                  <h2 className="mt-2 font-display text-lg leading-snug font-semibold">{p.title}</h2>
                  <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{p.excerpt}</p>
                  <div className="mt-3 flex items-center gap-4 text-xs text-muted-foreground">
                    <span className="inline-flex items-center gap-1">
                      <CalendarDays className="size-3.5" />
                      {new Date(p.published_at ?? p.created_at).toLocaleDateString("id-ID", {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                      })}
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <Eye className="size-3.5" /> {p.views}
                    </span>
                  </div>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
